import { ChangeDetectorRef, Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { SetupService } from '../../../services/setup/setup.service';
import {
  LocationOption,
  LocationSelectOptions,
  PhaseSelectOptions,
  PhaseSetupOption,
  ProjectSelectOptions,
  ProjectSetupOption,
  SetupChangeLogRecord,
  SetupChangeType,
  SetupMetadata,
  StageOption,
  StageSelectOptions,
  UpdatedSetupSummary,
  VesselOption,
} from '../../../models/setup/setup.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import {
  DailyLogType,
  DailyOperationLogRecord
} from '../../../models/projects/operation.models';
import { ValidationResult } from '../../../models/projects/validation.models';
import { SetupValidatorService } from '../../../services/setup/setup-validator.service';
import { DateService } from '../../../services/date/date.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { AppData } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import {
  TimeZoneChangeEditor,
  TimeZoneFilters
} from '../../../models/time-zones/time-zone.models';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { ProjectDetailsPermissions } from '../../../models/permissions/permission.models';
import { Subject, timer } from 'rxjs';
import { catchError, debounce } from 'rxjs/operators';
import { VesselService } from '../../../services/vessel/vessel.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { LocalSaveService } from '../../../services/local/local-save.service';
import { cloneDeep } from 'lodash';
import { DefaultPage } from '../../default/default-page/default-page';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { MetadataService } from "../../../services/metadata/metadata-service.service";
import { ProjectDetailPhaseGridPage } from '../project-details/project-detail-phase-grid/project-detail-phase-grid';
import { ImplementerService } from '../../../services/implementer/implementer.service';

@IonicPage()
@Component({
  selector: 'page-configure-project',
  templateUrl: 'configure-project.html',
})
export class ConfigureProjectPage {
  config = AppConfig.settings;
  loading: boolean;
  canEditProject = false;
  warnedUser = false;

  metadata: SetupMetadata;
  summary: UpdatedSetupSummary;
  summaryPrevious: UpdatedSetupSummary;
  vesselEditor: VesselOption;

  dailyLogDate: DateTime;
  startDate: DateTime; // used for the first time the user is setting up the app
  maxDate: DateTime; // used for the first time the user is setting up the app
  currentTimeChanged: string;
  currentTimeChangedPtLuxonDate: PtLuxonDate;
  currentLocalTime: string;

  isWeb: boolean;

  validationResult: ValidationResult = new ValidationResult();

  projectSelectOptions: any = new ProjectSelectOptions().options;
  phaseSelectOptions: any = new PhaseSelectOptions().options;
  stageSelectOptions: any = new StageSelectOptions().options;
  locationSelectOptions: any = new LocationSelectOptions().options;

  originalLocationName: string;

  projectOptions: ProjectSetupOption[] = [];
  phaseOptions: PhaseSetupOption[] = [];
  stageOptions: StageOption[] = [];
  locationOptions: LocationOption[] = [];

  projectTimeChanged: string;
  phaseTimeChanged: string;
  stageTimeChanged: string;
  locationTimeChanged: string;
  vesselMasterTimeChanged: string;

  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  timeZoneChangeEditor: TimeZoneChangeEditor = new TimeZoneChangeEditor();

  permissions: ProjectDetailsPermissions = new ProjectDetailsPermissions();

  $VesselMasterChange: Subject<string> = new Subject<string>();
  debounceActive: boolean = false;

  dailyLogUniqueOrdinal: number;
  dailyLogTempId: number;
  setupChangeTempId: number;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public setupService: SetupService,
    public operationService: OperationService,
    public setupValidationService: SetupValidatorService,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    public platform: Platform,
    public navHelperService: NavHelperService,
    private modalController: ModalController,
    private toastCtrl: ToastController,
    private appDataService: AppDataSyncService,
    private timeZoneService: TimeZoneService,
    public permissionService: PermissionsService,
    private vesselService: VesselService,
    private lockedDayService: LockedDayService,
    private localSaveService: LocalSaveService,
    private completeDayService: CompleteDayService,
    private metaDataService: MetadataService,
    private implementerService: ImplementerService
  ) {
  }

  ionViewWillLoad() {
    this.loading = true;
  }

  async ionViewDidLoad() {
    const date = new PtLuxonDate();

    this.isWeb = this.platform.is('core');

    this.permissions = await this.permissionService.projectDetailsPermissions();

    this.$VesselMasterChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(250)))
      .subscribe(val => {
        this.checkOperationsValid();
      });

    const timeZones = await this.timeZoneService.getAllTimeZones();

    const timeZoneFilters = new TimeZoneFilters();
    // if no timezones then  first app setup, just use the 'date' variable, as we don't want to load from localstorage
    const ptLuxonDate = timeZones && timeZones.length > 0
      ? await this.dailyLogService.getCurrentLogDay(false)
      : cloneDeep(date) as PtLuxonDate;

    this.dailyLogDate = ptLuxonDate.dateTime;
    timeZoneFilters.dateTime = this.startDate || this.dailyLogDate;
    const timeZone = await this.timeZoneService.getEditorLocally(timeZoneFilters);

    if (!!timeZone) {
      this.currentLocalTime = this.dailyLogDate.toFormat('HH:mm');
    } else {
      const yesterday = this.dailyLogDate.minus({days: 1});
      this.currentLocalTime = yesterday.toFormat('HH:mm');
      this.timeZoneChangeEditor.dateTime = yesterday;
    }

    this.vesselEditor = await this.vesselService.getEditor();

    this.metadata = await this.setupService.getSetupMetadata(this.vesselEditor.id, this.dailyLogDate, !timeZone);

    this.summary = new UpdatedSetupSummary();

    this.setupService.getSetupSummary(new PtLuxonDate(this.dailyLogDate), true)
      .then(async (updatedSummary) => {
        this.summary = this.setupService.assignEditorFromSummary(updatedSummary, this.summary);

        await this.assignEditorPrevious();

        if (!this.summaryPrevious.projectId) {
          // if first time setup, create luxonDate of local date (so we get the local offset), then set hours + minutes to 00:00
          const localDate = new PtLuxonDate(null, null, true);
          localDate.setTimeOnly(0, 0);
          date.setTimeOnly(0, 0); // also set this to 00:00, as user cannot edit time on first setup
          this.startDate = localDate.dateTime;
          this.maxDate = localDate.dateTime;
        }

        this.currentTimeChanged = date.dateTime.toFormat('HH:mm');
        // if no previous proj id, then use local offset when creating time (this will occur when setting up the app for the first time)
        this.currentTimeChangedPtLuxonDate = await this.dateService
          .setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate);

        const projectOptions = this.metadata.projectOptions.sort(function (a, b) {
          return a.ordinal - b.ordinal;
        });

        this.projectOptions = projectOptions;
        const project = projectOptions.find(e => e.id === this.summary.projectId);
        if (project) {
          // if we have a project set the phase options
          this.phaseOptions = project.phaseOptions;
          const phase = this.phaseOptions.find(e => e.id === this.summary.phaseId);
          if (phase) {
            // if we have a phase set the stage options
            this.stageOptions = phase.stageOptions;
            const stage = this.stageOptions.find(e => e.id === this.summary.stageId);
            if (stage) {
              // if we have a staeg set the location options
              this.locationOptions = stage.locationOptions;
            }
          }
        }

        await this.refreshEditAccess();
        this.loading = false;
      });
  }

  private async refreshEditAccess(): Promise<boolean> {
    this.canEditProject = false;
    try {
      this.permissions = await this.permissionService.projectDetailsPermissions();
      if (!this.permissions.canManageProjects) { return false; }

      // A historical day without a project is not first-time vessel setup.
      const timeZones = await this.timeZoneService.getAllTimeZones();
      if (!this.summaryPrevious?.projectId && timeZones?.length === 0 && !this.config.isImplementerEnvironment) {
        this.canEditProject = true;
        return true;
      }
      if (!this.dailyLogDate || !this.summaryPrevious?.projectId) { return false; }

      const date = new PtLuxonDate(this.dailyLogDate);
      if (this.config.isImplementerEnvironment) {
        const [active, completed, permissions] = await Promise.all([
          this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(date)),
          this.completeDayService.checkDay({ date: this.dailyLogDate }, true),
          this.permissionService.lockedDayPermissions(date)
        ]);
        this.canEditProject = active === true && completed === false
          && permissions?.functionalPermissions?.canImpactFutureDay === true;
      } else {
        // This checks both the recent-day window and whether Edit Day has
        // reopened a completed day. Older days require the implementer.
        const access = await this.lockedDayService.checkDay({
          date: this.dailyLogDate, userId: null, history: null
        });
        this.canEditProject = access?.lockedByDate === true;
      }
    } catch (_) {
      // Leave the page read-only when access cannot be verified.
      this.canEditProject = false;
    }
    return this.canEditProject;
  }

  async startDateChanged() {
    if (!this.startDate) {
      return;
    }

    this.loading = true;
    const startDateLocal = this.startDate;
    startDateLocal.set({
      year: startDateLocal.year,
      month: startDateLocal.month,
      day: startDateLocal.day,
      hour: 0,
      minute: 0
    });

    this.metadata = await this.setupService.getSetupMetadata(this.vesselEditor.id, startDateLocal, !this.summaryPrevious.projectId)
    const projectOptions = this.metadata.projectOptions.sort(function (a, b) {
      return a.ordinal - b.ordinal;
    });

    this.projectOptions = projectOptions;
    const project = projectOptions.find(e => e.id === this.summary.projectId);

    if (project) {
      // if we have a project set the phase options
      this.phaseOptions = project.phaseOptions;
      const phase = this.phaseOptions.find(e => e.id === this.summary.phaseId);
      if (phase) {
        // if we have a phase set the stage options
        this.stageOptions = phase.stageOptions;
        const stage = this.stageOptions.find(e => e.id === this.summary.stageId);
        if (stage) {
          // if we have a stage set the location options
          this.locationOptions = stage.locationOptions;
        } else {
          // if no stage after date change, then stage is invalid due to EF date
          this.summary.stageId = null;
          this.summary.locationId = null;
          this.summary.selectedLocation = null;
        }
      } else {
        // if no phase after date change, then phase is invalid due to EF date
        this.summary.stageId = null;
        this.summary.phaseId = null;
        this.summary.locationId = null;
        this.summary.selectedLocation = null;
      }
    } else {
      // if no project after date change, then project is invalid due to EF date
      this.summary.projectId = null;
      this.summary.stageId = null;
      this.summary.phaseId = null;
      this.summary.locationId = null;
      this.summary.selectedLocation = null;
    }

    this.loading = false;
  }

  async assignEditorPrevious() {
    this.summaryPrevious = Object.assign({}, this.summary);

    this.summaryPrevious.locationTimeChanged = PtLuxonDate.assignPtLuxonDate(this.summaryPrevious.locationTimeChanged);
    this.summaryPrevious.stageTimeChanged = PtLuxonDate.assignPtLuxonDate(this.summaryPrevious.stageTimeChanged);
    this.summaryPrevious.phaseTimeChanged = PtLuxonDate.assignPtLuxonDate(this.summaryPrevious.phaseTimeChanged);
    this.summaryPrevious.projectTimeChanged = PtLuxonDate.assignPtLuxonDate(this.summaryPrevious.projectTimeChanged);
    this.summaryPrevious.vesselMasterTimeChanged = PtLuxonDate.assignPtLuxonDate(this.summaryPrevious.vesselMasterTimeChanged);

    this.locationTimeChanged = await this.dateService.setFullTimeString(this.summaryPrevious.locationTimeChanged.dateTime);
    this.stageTimeChanged = await this.dateService.setFullTimeString(this.summaryPrevious.stageTimeChanged.dateTime);
    this.phaseTimeChanged = await this.dateService.setFullTimeString(this.summaryPrevious.phaseTimeChanged.dateTime);
    this.projectTimeChanged = await this.dateService.setFullTimeString(this.summaryPrevious.projectTimeChanged.dateTime);
    this.vesselMasterTimeChanged = await this.dateService.setFullTimeString(this.summaryPrevious.vesselMasterTimeChanged.dateTime);
  }

  projectChanged(id: number) {
    this.summary.projectId = id;
    const index = this.projectOptions.findIndex(e => e.id === id);

    this.summary.projectName = index !== -1 ? this.projectOptions[index].name : null;

    if (!this.summary.projectName) {
      this.summary.projectId = null;
    }

    this.summary.phaseId = null;
    this.summary.stageId = null;
    this.phaseOptions = index !== -1 ? this.projectOptions[index].phaseOptions.sort(function (a, b) {
      return a.ordinal - b.ordinal;
    }) : [];

    this.checkOperationsValid();
  }

  phaseChanged(id: number) {
    this.summary.phaseId = id;
    const index = this.phaseOptions.findIndex(e => e.id === id);

    this.summary.phaseName = index !== -1 ? this.phaseOptions[index].name : null;

    if (!this.summary.phaseName) {
      this.summary.phaseId = null;
    }

    this.summary.stageId = null;
    this.stageOptions = index !== -1 ? this.phaseOptions[index].stageOptions.sort(function (a, b) {
      return a.ordinal - b.ordinal;
    }) : [];

    this.checkOperationsValid();
  }

  stageChanged(id: number) {
    this.summary.stageId = id;
    const index = this.stageOptions.findIndex(e => e.id === id);

    this.summary.stageName = index !== -1 ? this.stageOptions[index].name : null;

    if (!this.summary.stageName) {
      this.summary.stageId = null;
    }

    this.checkLocationValid();

    this.checkOperationsValid();
  }

  locationChanged(id: number) {
    this.summary.locationId = id;
    const index = this.locationOptions.findIndex(e => e.id === id);

    this.summary.locationName = index !== -1 ? this.locationOptions[index].name : null;
    this.summary.selectedLocation = cloneDeep(this.locationOptions[index]);

    if (!this.summary.locationName) {
      this.summary.locationId = null;
      this.summary.selectedLocation = null;
    }

    if (!this.locationOptions[index]) {
      return;
    }

    this.checkOperationsValid();
  }

  vesselMasterChanged() {
    this.debounceActive = true;
    this.$VesselMasterChange.next();
  }

  vesselMasterValid(): boolean {
    if (this.validationResult.errors.some(e => e.message === 'You must enter a Vessel Master')) {
      return false;
    }

    return true;
  }

  checkLocationValid() {
    if (this.summary.stageId != null) {
      const stage = this.stageOptions.find(e => e.id === this.summary.stageId);
      this.locationOptions = stage ? stage.locationOptions : this.stageOptions[0].locationOptions;

      if (this.locationOptions.findIndex(e => e.id === this.summary.locationId) === -1) {
        if (this.summary.locationId !== null && this.summary.locationId !== undefined) {
          this.presentLocationRemovedToast();
        }
        this.summary.locationId = null;
        this.summary.selectedLocation = null;
      }
    }
  }

  async currentTimeEdited(luxonDate: PtLuxonDate) {
    this.currentTimeChangedPtLuxonDate = luxonDate;

    if (this.currentTimeChangedPtLuxonDate && this.currentTimeChangedPtLuxonDate.dateTime) {
      this.currentTimeChanged = this.currentTimeChangedPtLuxonDate.dateTime.toFormat('HH:mm');
    }

    this.checkOperationsValid();
  }

  async checkOperationsValid() {
    let dateTimeChanged = new PtLuxonDate();

    if (!!this.currentTimeChanged && !!this.summaryPrevious.projectId) {
      dateTimeChanged = this.currentTimeChangedPtLuxonDate && this.currentTimeChangedPtLuxonDate.dateTime
        ? this.currentTimeChangedPtLuxonDate
        : await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate);
    }

    this.validationResult = await this.setupValidationService.validateSetup(this.summary, dateTimeChanged);

    if (this.changed(this.summary, this.summaryPrevious)) {
      await this.setupValidationService.checkNewerOperations(dateTimeChanged.dateTime, this.summary.stageId, this.validationResult)
        .then(validationResult => {
          if (!this.validationResult.isValid) {
            this.warnedUser = true;
            return;
          }
        });
    }

    this.setupValidationService
      .validateLocation(this.summaryPrevious.locationName, this.summary.locationName, this.validationResult);
  }

  changed(summary: UpdatedSetupSummary, summaryPrevious: UpdatedSetupSummary): boolean {
    if (!summaryPrevious || !summary) {
      return false;
    }

    if (summary.projectId !== summaryPrevious.projectId) {
      return true;
    }

    if (summary.phaseId !== summaryPrevious.phaseId) {
      return true;
    }

    if (summary.stageId !== summaryPrevious.stageId) {
      return true;
    }

    return false;
  }

  async save() {
    if (this.loading) { return; }
    this.loading = true;

    // Recheck before any local writes, operation deletion, or API saves.
    if (!await this.refreshEditAccess()) {
      this.loading = false;
      this.toastCtrl.create({
        message: 'Project changes are not allowed for this day. Open the Daily Log to check editing access.',
        duration: 6000, position: 'bottom'
      }).present();
      return;
    }

    if (!this.validationResult.isValid) {
      this.loading = false;
      return;
    }

    if (!this.summaryPrevious.projectId) {
      // Only when setting up a new project
      if (!!this.timeZoneChangeEditor.dateTime && this.currentLocalTime != null && this.currentLocalTime.length === 5) {

        // When creating a new project this.currentLocalTime equals this.currentTimeChanged
        // A new project setup must always have a start date
        if (this.startDate) {
          this.timeZoneChangeEditor.dateTime = this.startDate;
        }

        const startDate = this.timeZoneChangeEditor.dateTime;
        startDate.set({
          year: startDate.year,
          month: startDate.month,
          day: startDate.day,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        });

        // On project setup we use the client timezone.
        this.timeZoneChangeEditor.secondsOffsetFromUtc = this.startDate.offset.valueOf() * 60; // .offset returns minutes so we convert to seconds
        // for the first ever timezone editor the previousOffset can just be assigned the same as the current
        // - we need it to be populated in some places
        this.timeZoneChangeEditor.previousSecondsOffsetFromUtc = this.timeZoneChangeEditor.secondsOffsetFromUtc;

        // If user is setting up the app in a timezone that isn't +0, then first add a timezone change of +0 on the previous day as a base
        // reading, then save another timezone change, again on the previous day of +(whatever their local offset is), and set that day as
        // completed so it starts the app on the correct day. This is a workaround to let them start the app in whatever zone they want.
        if (this.timeZoneChangeEditor.secondsOffsetFromUtc !== 0) {
          const defaultEditor = new TimeZoneChangeEditor();
          defaultEditor.secondsOffsetFromUtc = 0;
          defaultEditor.previousSecondsOffsetFromUtc = 0;

          defaultEditor.dateTime = startDate.minus({days: 1});

          this.timeZoneChangeEditor.previousSecondsOffsetFromUtc = defaultEditor.secondsOffsetFromUtc;
          this.timeZoneChangeEditor.dateTime = defaultEditor.dateTime.plus({minute: 1});

          await this.completeDayService.saveCompleteDay({
            ptLuxonDate: new PtLuxonDate(defaultEditor.dateTime),
            isComplete: true,
            isSignedOff: true,
            isDefault: true,
            validationErrors: null,
            errorsJson: null,
            errorCount: 0,
            warningCount: 0,
            localDate: null
          }, false);
          await this.timeZoneService
            .saveEditor(defaultEditor, true)
            .then();
        }

        await this.timeZoneService
          .saveEditor(this.timeZoneChangeEditor, true)
          .then();

        // need to set this again here - as it's possible it could have changed in the previous if(), and this is needed for the daily log
        // records that get created below
        this.timeZoneChangeEditor.dateTime = startDate;

        const instances = await this.operationService.getAllProjectSpecificOperationInstances(null);
        const logs = await this.dailyLogService.getDailyLogData();
        const appData = new AppData();
        appData.dailyLogOperationInstances = instances;
        appData.dailyLogRecords = logs;
        appData.cleanseLogs = false;
        await this.appDataService.saveAppData(appData);
      }
    }

    let dateTimeChanged = new PtLuxonDate();

    // if first setup use the tz editor, else use the currentTimeChangedPtLuxonDate instead of just the string so that any offset
    // change is correctly saved.
    if (this.summaryPrevious.projectId && this.currentTimeChangedPtLuxonDate && this.currentTimeChangedPtLuxonDate.dateTime) {
      dateTimeChanged = this.currentTimeChangedPtLuxonDate;
    } else if (this.currentTimeChanged) {
      if (this.timeZoneChangeEditor && this.timeZoneChangeEditor.dateTime) {
        // if timezone change edit.date exists then it is the initial setup
        dateTimeChanged = new PtLuxonDate(this.timeZoneChangeEditor.dateTime);
      } else {
        dateTimeChanged = await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.startDate || this.dailyLogDate);
      }
    }

    if (this.valueChanged('project')) {
      try {
        const validation = await this.appDataService.validateCycleProjectChange(this.summary.projectId, dateTimeChanged.dateTime);
        if (!validation.canChange) {
          this.loading = false;
          this.toastCtrl.create({message: validation.message, duration: 8000, position: 'bottom'}).present();
          return;
        }
      } catch (error) {
        this.loading = false;
        this.toastCtrl.create({message: 'The project change could not be validated. Please try again.',
          duration: 8000, position: 'bottom'}).present();
        return;
      }
    }

    if (this.changed(this.summary, this.summaryPrevious) === true) {
      this.setupValidationService.deleteNewerOperations(dateTimeChanged.dateTime, this.summary.stageId);
    }

    this.summary.projectTimeChanged = this.valueChanged('project') ? dateTimeChanged : this.summaryPrevious.projectTimeChanged;
    this.summary.phaseTimeChanged = this.valueChanged('phase') ? dateTimeChanged : this.summaryPrevious.phaseTimeChanged;
    this.summary.stageTimeChanged = this.valueChanged('stage') ? dateTimeChanged : this.summaryPrevious.stageTimeChanged;
    this.summary.locationTimeChanged = this.valueChanged('location') ? dateTimeChanged : this.summaryPrevious.locationTimeChanged;
    this.summary.vesselMasterTimeChanged = this.valueChanged('master') ? dateTimeChanged : this.summaryPrevious.vesselMasterTimeChanged;

    await this.setupService.saveSetup(this.summary);
    const operationMetadata = await this.operationService.getOperationsMetadata((await this.dailyLogService.getCurrentLogDay(null, true)));
    this.operationService.saveOperationsData(operationMetadata);

    this.navHelperService.updateSetupConfigured(true);

    let appDataBlob = new AppData();
    const dailyLogRecordCount = this.getRequiredDailyLogRecordsCount();
    this.dailyLogUniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(dailyLogRecordCount);
    this.dailyLogTempId = await this.operationService.getDailyLogRecordTempId();
    this.setupChangeTempId = await this.setupService.getSetupChangeLogRecordTempId();

    appDataBlob = await this.saveProjectChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.savePhaseChange(appDataBlob,  dateTimeChanged);

    appDataBlob = await this.saveStageChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveLocationChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveVesselMasterChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveClientRepNameChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveClientRepTitleChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveOtherRepNameChange(appDataBlob, dateTimeChanged);

    appDataBlob = await this.saveOtherRepTitleChange(appDataBlob, dateTimeChanged);

    if (appDataBlob.dailyLogRecords.length > 0 || appDataBlob.setupChangeLogRecords.length > 0) {
      await this.appDataService.syncLocalData(appDataBlob);
      await this.assignEditorPrevious();
    }

    this.presentSaveToast().then(() => {
      this.metaDataService.getMetadata().catch();
      this.navCtrl.setRoot(DefaultPage);
      this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
    });

    this.loading = false;
  }

  clearTimeOfChange() {
    this.currentTimeChanged = null;
  }

  valueChanged(type: string): boolean {
    switch (type) {
      case 'project':
        return this.summary.projectId !== this.summaryPrevious.projectId;
      case 'phase':
        return this.summary.phaseId !== this.summaryPrevious.phaseId;
      case 'stage':
        return this.summary.stageId !== this.summaryPrevious.stageId;
      case 'location':
        return this.summary.locationId !== this.summaryPrevious.locationId;
      case 'master':
        return this.summary.vesselMasterName !== this.summaryPrevious.vesselMasterName;
      case 'clientRepName':
        return this.summary.clientRepName !== this.summaryPrevious.clientRepName;
      case 'clientRepTitle':
        return this.summary.clientRepTitle !== this.summaryPrevious.clientRepTitle;
      case 'otherRepName':
        return this.summary.otherRepName !== this.summaryPrevious.otherRepName;
      case 'otherRepTitle':
        return this.summary.otherRepTitle !== this.summaryPrevious.otherRepTitle;
      default:
        return true;
    }
  }

  private async createDailyLogRecord(
    classification: string,
    previousName: string,
    newName: string,
    id: number,
    uniqueOrdinal: number,
    type: DailyLogType,
    ordinal: number,
    localSetupChangeLogId: number,
    dateOverride?: PtLuxonDate
  ): Promise<DailyOperationLogRecord> {
    const startTime = dateOverride
      || await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.startDate || this.dailyLogDate);
    startTime.setTimeOnly(startTime.dateTime.hour, startTime.dateTime.minute, startTime.dateTime.second, 0);

    let operationName = 'To ' + newName;

    if (previousName !== null && previousName !== undefined) {
      operationName = 'From ' + previousName + ' ' + operationName;
    }

    const record: DailyOperationLogRecord = {
      id: null,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      localId: <number>id,
      localSetupChangeLogRecordId: localSetupChangeLogId,
      guid: '',
      isContractual: true,
      contractualConcurrentText: '',
      operationClassification: '',
      isInterruption: false,
      lineTypeName: classification + ' Change',
      startTime: startTime,
      endTime: null,
      comment: null,
      stepInstance: null,
      operationName: operationName,
      startTimeShow: this.currentTimeChanged,
      endTimeShow: null,
      startTimeOffset: startTime.dateTime.offset / 60,
      endTimeOffset: null,
      commentAppearOnDPR: null,
      operationInstanceId: null,
      stageIds: null,
      sortTime: startTime.dateTime,
      typeId: type,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: false,
      ordinal: ordinal,
      uniqueOrdinal: uniqueOrdinal,
      stepNumber: null,
      commentIdentifier: null,
      operationStartTime: startTime,
      archived: false,
      secondaryGuid: null,
      canConfirmTimes: true,
      customSort: null
    };

    record.sortTime = record.sortTime.set({
      year: record.sortTime.year,
      month: record.sortTime.month,
      day: record.sortTime.day,
      hour: record.sortTime.hour,
      minute: record.sortTime.minute,
      second: record.sortTime.second,
      millisecond: 0
    });

    return record;
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Setup Saved',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  presentLocationRemovedToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Location removed as it is not applicable to the selected stage',
      duration: 5000,
      position: 'bottom'
    });

    return toast.present();
  }

  private getRequiredDailyLogRecordsCount(): number {
    let count = 0;

    if (this.valueChanged('project')) {
      count++;
    }

    if (this.valueChanged('phase')) {
      count++;
    }

    if (this.valueChanged('stage')) {
      count++;
    }

    if (this.valueChanged('location')) {
      count++;
    }

    if (this.valueChanged('master')) {
      count++;
    }

    if (this.valueChanged('clientRepName')) {
      count++;
    }

    if (this.valueChanged('clientRepTitle')) {
      count++;
    }

    if (this.valueChanged('otherRepName')) {
      count++;
    }

    if (this.valueChanged('otherRepTitle')) {
      count++;
    }

    return count;
  }

  public openModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  searchCountry($event: Event) {

  }

  viewProjectDetails() {
    this.navCtrl.push(ProjectDetailPhaseGridPage, {projectId: this.summary.projectId, projectName: this.summary.projectName});
  }

  private async saveProjectChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    const previousActiveRecord = await this.setupService.getPreviousSetupChangeLogRecord(
      await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate),
      SetupChangeType.ProjectChange);

    // save project change
    const projectChange: SetupChangeLogRecord = {
      id: null,
      localId: this.setupChangeTempId,
      changeType: SetupChangeType.ProjectChange,
      changeTypeId: SetupChangeType.ProjectChange,
      previousValue: previousActiveRecord ? previousActiveRecord.newValue : null,
      previousValueId: previousActiveRecord ? previousActiveRecord.newValueId : null,
      newValue: this.summary.projectName,
      newValueId: this.summary.projectId,
      timeOfChange: dateTimeChanged,
      timeOfChangeToShow: this.currentTimeChanged,
      // we want this time of change to be in hours
      timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
      dailyLogRecordId: null,
      localDailyLogRecordId: this.dailyLogTempId,
      batchId: null
    };

    if (this.valueChanged('project')) {
      // save setup change as a record in the daily log
      const projectDailyLogRecord = await this.createDailyLogRecord(
        'Project',
        this.summaryPrevious.projectName,
        this.summary.projectName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.ProjectChange,
        2,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(projectDailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
      appDataBlob.setupChangeLogRecords.push(projectChange);
    }

    return appDataBlob;
  }

  private async savePhaseChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    // save phase change
    const previousActiveRecord = await this.setupService.getPreviousSetupChangeLogRecord(
      await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate),
      SetupChangeType.PhaseChange);

    const phaseChange: SetupChangeLogRecord = {
      id: null,
      localId: this.setupChangeTempId,
      changeType: SetupChangeType.PhaseChange,
      changeTypeId: SetupChangeType.PhaseChange,
      previousValue: previousActiveRecord ? previousActiveRecord.newValue : null,
      previousValueId: previousActiveRecord ? previousActiveRecord.newValueId : null,
      newValue: this.summary.phaseName,
      newValueId: this.summary.phaseId,
      timeOfChange: dateTimeChanged,
      timeOfChangeToShow: this.currentTimeChanged,
      timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
      dailyLogRecordId: null,
      localDailyLogRecordId: this.dailyLogTempId,
      batchId: null
    };

    if (this.valueChanged('phase')) {
      const phaseDailyLogRecord = await this.createDailyLogRecord(
        'Phase',
        this.summaryPrevious.phaseName,
        this.summary.phaseName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.PhaseChange,
        3,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(phaseDailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
      appDataBlob.setupChangeLogRecords.push(phaseChange);
    }

    return appDataBlob;
  }

  private async saveStageChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    // save stage change
    const previousActiveRecord = await this.setupService.getPreviousSetupChangeLogRecord(
      await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate),
      SetupChangeType.StageChange);

    const stageChange: SetupChangeLogRecord = {
      id: null,
      localId: this.setupChangeTempId,
      changeType: SetupChangeType.StageChange,
      changeTypeId: SetupChangeType.StageChange,
      previousValue: previousActiveRecord ? previousActiveRecord.newValue : null,
      previousValueId: previousActiveRecord ? previousActiveRecord.newValueId : null,
      newValue: this.summary.stageName,
      newValueId: this.summary.stageId,
      timeOfChange: dateTimeChanged,
      timeOfChangeToShow: this.currentTimeChanged,
      timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
      dailyLogRecordId: null,
      localDailyLogRecordId: this.dailyLogTempId,
      batchId: null
    };

    if (this.valueChanged('stage')) {
      const stageDailyLogRecord = await this.createDailyLogRecord(
        'Stage',
        this.summaryPrevious.stageName,
        this.summary.stageName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.StageChange,
        4,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(stageDailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
      appDataBlob.setupChangeLogRecords.push(stageChange);
    }

    return appDataBlob;
  }

  private async saveLocationChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    // save location changed
    const previousActiveRecord = await this.setupService.getPreviousSetupChangeLogRecord(
      await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate),
      SetupChangeType.LocationChange);

    const locationChange: SetupChangeLogRecord = {
      id: null,
      localId: this.setupChangeTempId,
      changeType: SetupChangeType.LocationChange,
      changeTypeId: SetupChangeType.LocationChange,
      previousValue: previousActiveRecord ? previousActiveRecord.newValue : null,
      previousValueId: previousActiveRecord ? previousActiveRecord.newValueId : null,
      newValue: this.summary.locationName,
      newValueId: this.summary.locationId,
      timeOfChange: dateTimeChanged,
      timeOfChangeToShow: this.currentTimeChanged,
      timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
      dailyLogRecordId: null,
      localDailyLogRecordId: this.dailyLogTempId,
      batchId: null
    };

    if (this.valueChanged('location')) {
      const locationDailyLogRecord = await this.createDailyLogRecord(
        'Location',
        this.summaryPrevious.locationName,
        this.summary.locationName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.LocationChange,
        4,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(locationDailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
      appDataBlob.setupChangeLogRecords.push(locationChange);
    }

    return appDataBlob;
  }

  private async saveVesselMasterChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    // save vessel master change
    const previousActiveRecord = await this.setupService.getPreviousSetupChangeLogRecord(
      await this.dateService.setPtLuxonDateFromUserEnteredString(this.currentTimeChanged, this.dailyLogDate),
      SetupChangeType.VesselMasterChange);

    const vesselMasterChange: SetupChangeLogRecord = {
      id: null,
      localId: this.setupChangeTempId,
      changeType: SetupChangeType.VesselMasterChange,
      changeTypeId: SetupChangeType.VesselMasterChange,
      previousValue: previousActiveRecord ? previousActiveRecord.newValue : null,
      previousValueId: null,
      newValue: this.summary.vesselMasterName,
      newValueId: null,
      timeOfChange: dateTimeChanged,
      timeOfChangeToShow: this.currentTimeChanged,
      timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
      dailyLogRecordId: null,
      localDailyLogRecordId: this.dailyLogTempId,
      batchId: null
    };

    if (this.valueChanged('master')) {
      const vesselMasterDailyLogRecord = await this.createDailyLogRecord(
        'Vessel Master',
        this.summaryPrevious.vesselMasterName,
        this.summary.vesselMasterName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.VesselMasterChanged,
        6,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(vesselMasterDailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
      appDataBlob.setupChangeLogRecords.push(vesselMasterChange);
    }

    return appDataBlob;
  }

  async saveClientRepNameChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate): Promise<AppData> {
    if (this.valueChanged('clientRepName')) {

      appDataBlob.setupChangeLogRecords.push({
        id: null,
        localId: this.setupChangeTempId,
        changeType: SetupChangeType.ClientRepChange,
        changeTypeId: SetupChangeType.ClientRepChange,
        previousValue: null,
        previousValueId: null,
        newValue: this.summary.clientRepName,
        newValueId: null,
        timeOfChange: dateTimeChanged,
        timeOfChangeToShow: this.currentTimeChanged,
        timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
        dailyLogRecordId: null,
        localDailyLogRecordId: this.dailyLogTempId,
        batchId: null
      });

      // save setup change as a record in the daily log
      const dailyLogRecord = await this.createDailyLogRecord(
        'Client Rep',
        this.summaryPrevious.clientRepName,
        this.summary.clientRepName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.OtherSetupChange,
        6,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(dailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
    }

    return appDataBlob;
  }

  private async saveClientRepTitleChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate): Promise<AppData> {
    if (this.valueChanged('clientRepTitle')) {
      appDataBlob.setupChangeLogRecords.push({
        id: null,
        localId: this.setupChangeTempId,
        changeType: SetupChangeType.ClientRepTitleChange,
        changeTypeId: SetupChangeType.ClientRepTitleChange,
        previousValue: null,
        previousValueId: null,
        newValue: this.summary.clientRepTitle,
        newValueId: null,
        timeOfChange: dateTimeChanged,
        timeOfChangeToShow: this.currentTimeChanged,
        timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
        dailyLogRecordId: null,
        localDailyLogRecordId: this.dailyLogTempId,
        batchId: null
      });

      // save setup change as a record in the daily log
      const dailyLogRecord = await this.createDailyLogRecord(
        'Client Rep Title',
        this.summaryPrevious.clientRepTitle,
        this.summary.clientRepTitle,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.OtherSetupChange,
        6,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(dailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
    }

    return appDataBlob;
  }

  private async saveOtherRepNameChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    if (this.valueChanged('otherRepName')) {
      appDataBlob.setupChangeLogRecords.push({
        id: null,
        localId: this.setupChangeTempId,
        changeType: SetupChangeType.OtherRepChange,
        changeTypeId: SetupChangeType.OtherRepChange,
        previousValue: null,
        previousValueId: null,
        newValue: this.summary.otherRepName,
        newValueId: null,
        timeOfChange: dateTimeChanged,
        timeOfChangeToShow: this.currentTimeChanged,
        timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
        dailyLogRecordId: null,
        localDailyLogRecordId: this.dailyLogTempId,
        batchId: null
      });

      // save setup change as a record in the daily log
      const dailyLogRecord = await this.createDailyLogRecord(
        'Other Rep',
        this.summaryPrevious.otherRepName,
        this.summary.otherRepName,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.OtherSetupChange,
        6,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(dailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
    }

    return appDataBlob;
  }

  private async saveOtherRepTitleChange(appDataBlob: AppData, dateTimeChanged: PtLuxonDate) {
    if (this.valueChanged('otherRepTitle')) {
      appDataBlob.setupChangeLogRecords.push({
        id: null,
        localId: this.setupChangeTempId,
        changeType: SetupChangeType.OtherRepTitleChange,
        changeTypeId: SetupChangeType.OtherRepTitleChange,
        previousValue: null,
        previousValueId: null,
        newValue: this.summary.otherRepTitle,
        newValueId: null,
        timeOfChange: dateTimeChanged,
        timeOfChangeToShow: this.currentTimeChanged,
        timeOfChangeOffset: dateTimeChanged.dateTime.offset / 60,
        dailyLogRecordId: null,
        localDailyLogRecordId: this.dailyLogTempId,
        batchId: null
      });

      // save setup change as a record in the daily log
      const dailyLogRecord = await this.createDailyLogRecord(
        'Other Rep Title',
        this.summaryPrevious.otherRepTitle,
        this.summary.otherRepTitle,
        this.dailyLogTempId,
        this.dailyLogUniqueOrdinal,
        DailyLogType.OtherSetupChange,
        6,
        this.setupChangeTempId,
        dateTimeChanged);

      appDataBlob.dailyLogRecords.push(dailyLogRecord);
      this.dailyLogTempId--;
      this.setupChangeTempId--;
      this.dailyLogUniqueOrdinal++;
    }

    return appDataBlob;
  }
}



// WEBPACK FOOTER //
// ./src/pages/setup/configure-project/configure-project.ts